// Typed error for every non-2xx response and for client-side aborts. Views map
// these to HonestState variants through states/stateOf.ts.

export const CLIENT_ERROR_CODES = {
  timeout: 'request_timeout',
  cancelled: 'request_cancelled',
  network: 'network_unreachable',
  csrfUnavailable: 'csrf_unavailable',
  invalidJson: 'response_not_json',
  requestFailed: 'request_failed',
} as const;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  // What the server's Retry-After header asked for, in milliseconds, or null
  // when it sent none. Read by withRetry so a rate limit waits the window out.
  readonly retryAfterMs: number | null;

  constructor(status: number, code: string, message: string, retryAfterMs: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfterMs = retryAfterMs;
  }

  get isTimeout(): boolean {
    return this.code === CLIENT_ERROR_CODES.timeout;
  }

  get isNetwork(): boolean {
    return this.code === CLIENT_ERROR_CODES.network;
  }

  get isServerSide(): boolean {
    return this.status >= 500;
  }

  // 429 is a load artefact on this backend, not a final answer: the same
  // cookie that drew a 429 answers 200 seconds later (docs/DATA-REALITY
  // -2026-08-19, "Load behaviour"). Treating it as final ejected a signed-in
  // owner to the sign-in door on one unlucky boot.
  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export function toApiError(error: unknown, fallbackMessage: string): ApiError {
  if (error instanceof ApiError) return error;
  if (error instanceof DOMException && error.name === 'AbortError') {
    return new ApiError(0, CLIENT_ERROR_CODES.timeout, 'The request was aborted before the server answered.');
  }
  if (error instanceof TypeError) {
    return new ApiError(0, CLIENT_ERROR_CODES.network, 'The browser could not reach the workspace service.');
  }
  return new ApiError(0, CLIENT_ERROR_CODES.requestFailed, error instanceof Error ? error.message : fallbackMessage);
}

// Retry-After is either delay-seconds or an HTTP date (RFC 9110 10.2.3).
// Anything else is unreadable and the caller's own schedule decides.
export function parseRetryAfter(header: string | null | undefined, now: number = Date.now()): number | null {
  if (!header) return null;
  const trimmed = header.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000;
  const at = Date.parse(trimmed);
  if (!Number.isFinite(at)) return null;
  return Math.max(0, at - now);
}

// Retry what a retry can fix: network failures, timeouts, 5xx, and 429.
export function isRetryableError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (!(error instanceof ApiError)) return false;
  return error.isNetwork || error.isTimeout || error.isServerSide || error.isRateLimited;
}
